import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { PagedDto } from '../../../models/paged.dto';
import { AuthService } from '../../../services/auth.service';
import { CaseCreateDialogComponent } from './case-create-dialog.component';

interface CaseListItem {
  id: number; customerId: number; customerName: string;
  title: string; status: string; assignedToUserName: string | null; createdAt: string;
}

@Component({
  selector: 'app-crm-cases',
  templateUrl: './cases.component.html',
  host: { class: 'workspace-page workspace-page--full' },
})
export class CrmCasesComponent implements OnInit, OnDestroy {
  @ViewChild('compactList') compactList?: DataListComponent<CaseListItem>;
  collectionSearch = '';

  /** A column id is its server sort key; the server allowlists what it takes. */
  readonly columns: DataTableColumn[] = [
    { id: 'title', label: 'Title' },
    { id: 'customerName', label: 'Customer' },
    { id: 'status', label: 'Status' },
    { id: 'assignedToUserName', label: 'Assigned To' },
    { id: 'createdAt', label: 'Created' },
  ];

  filterStatus = 'Open';
  filterAssigned: number | '' = '';
  users: { id: number; name: string }[] = [];
  private readonly destroyed$ = new Subject<void>();

  @ViewChild('table') table?: DataTableComponent<CaseListItem>;

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Cases'), name: 'Cases', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => ({ status: this.filterStatus, assigned: this.filterAssigned }),
      describe: state => JSON.stringify(state),
      restore: state => { this.filterStatus = state.status; this.filterAssigned = state.assigned; this.reload(); },
    });
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    public auth: AuthService,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.registerViewContext();
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Server-mode source. Search, sort and paging all reach the database, so the
   * list no longer depends on the first 5000 rows happening to contain what
   * someone is looking for.
   */
  fetch = (q: DataQuery): Observable<DataPage<CaseListItem>> => {
    let params = new HttpParams()
      .set('page', String(q.page))
      .set('pageSize', String(q.pageSize))
      .set('desc', String(q.desc ?? false));
    if (q.sort) params = params.set('sort', q.sort);
    if (q.search) params = params.set('search', q.search);
    if (this.filterStatus) params = params.set('status', this.filterStatus);
    if (this.filterAssigned !== '') {
      params = params.set('assignedToUserId', String(this.filterAssigned));
    }
    return this.http.get<PagedDto<CaseListItem>>('/api/cases', { params })
      .pipe(map(p => ({ items: p.items, total: p.total })));
  };

  /** A filter changed; the table re-asks with it. */
  reload(): void {
    this.table?.reload();
    this.compactList?.reload();
  }

  loadUsers(): void {
    // /api/users returns a bare array of users; usernames are email-style, so show
    // the local part as the name.
    this.http.get<{ id: number; userName: string | null }[]>('/api/users').subscribe({
      next: list => {
        this.users = (list ?? []).map(u => {
          const n = u.userName ?? `User ${u.id}`;
          return { id: u.id, name: n.includes('@') ? n.split('@')[0] : n };
        });
      },
      error: () => {}
    });
  }

  openNewCaseDialog(): void {
    const ref = this.dialog.open(CaseCreateDialogComponent, {
      width: '520px',
      data: {},
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
      if (result) this.reload();
    });
  }

  goToCase(id: number): void {
    this.router.navigate(['/cases', id]);
  }
}
